import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getManagers, createManager, updateManager, deleteManager, restoreManager, createUser, getUserByManager, updateUser, getTeams } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, UserCircle, Percent, Loader2, Key, Eye, EyeOff, Users, Archive, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

export default function ManagersPage() {
  const [managers, setManagers] = useState([]);
  const [archivedManagers, setArchivedManagers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [name, setName] = useState('');
  const [percentStandard, setPercentStandard] = useState('');
  const [percentAbove1000, setPercentAbove1000] = useState('');
  const [teamId, setTeamId] = useState('');
  
  // Login credentials for manager
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [existingUserId, setExistingUserId] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingManager, setDeletingManager] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [activeManagersData, allManagersData, teamsData] = await Promise.all([
        getManagers(false),
        getManagers(true),
        getTeams().catch(() => [])
      ]);
      setManagers(activeManagersData);
      // Filter to get only archived (inactive) managers
      const archived = allManagersData.filter(m => m.is_active === false);
      setArchivedManagers(archived);
      setTeams(teamsData);
    } catch (err) {
      console.error('Failed to fetch managers:', err);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/weeks');
      return;
    }
    fetchData();
  }, [fetchData, isAdmin, navigate]);

  const openCreateDialog = () => {
    setEditingManager(null);
    setName('');
    setPercentStandard('10');
    setPercentAbove1000('15');
    setTeamId('');
    setUsername('');
    setPassword('');
    setDialogOpen(true);
  };

  const openEditDialog = async (manager) => {
    setEditingManager(manager);
    setName(manager.name);
    setPercentStandard(manager.percent_standard.toString());
    setPercentAbove1000(manager.percent_above_1000.toString());
    setTeamId(manager.team_id || '');
    setUsername('');
    setPassword('');
    setExistingUserId(null);
    setDialogOpen(true);
    
    // Try to load existing user for this manager
    try {
      setLoadingUser(true);
      const user = await getUserByManager(manager.id);
      setExistingUserId(user.id);
      setUsername(user.username);
    } catch (err) {
      // No user found - that's ok
      console.log('No user found for manager');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleSave = async () => {
    if (!name || !percentStandard || !percentAbove1000) return;
    
    // For new manager, require login credentials
    if (!editingManager && (!username || !password)) {
      toast.error('Укажите логин и пароль для менеджера');
      return;
    }

    try {
      setSaving(true);
      const managerData = {
        name,
        percent_standard: parseFloat(percentStandard),
        percent_above_1000: parseFloat(percentAbove1000),
        team_id: teamId || null,
      };

      if (editingManager) {
        await updateManager(editingManager.id, managerData);
        
        // Update user credentials if provided
        if (existingUserId && (username || password)) {
          const userData = {};
          if (username) userData.username = username;
          if (password) userData.password = password;
          
          try {
            await updateUser(existingUserId, userData);
            toast.success('Менеджер и данные для входа обновлены');
          } catch (userErr) {
            console.error('Failed to update user:', userErr);
            toast.error(userErr.response?.data?.detail || 'Ошибка обновления данных для входа');
          }
        } else if (!existingUserId && username && password) {
          // Create new user if none exists
          try {
            await createUser({
              username: username,
              password: password,
              role: 'manager',
              manager_id: editingManager.id,
              team_id: teamId || null,
              is_active: true,
            });
            toast.success(`Менеджер обновлён! Создан логин: ${username}`);
          } catch (userErr) {
            console.error('Failed to create user:', userErr);
            toast.error(userErr.response?.data?.detail || 'Ошибка создания пользователя');
          }
        } else {
          toast.success('Менеджер обновлён');
        }
      } else {
        // Create manager first
        const newManager = await createManager(managerData);
        
        // Then create user account for this manager
        try {
          await createUser({
            username: username,
            password: password,
            role: 'manager',
            manager_id: newManager.id,
            team_id: teamId || null,
            is_active: true,
          });
          toast.success(`Менеджер создан! Логин: ${username}`);
        } catch (userErr) {
          console.error('Failed to create user:', userErr);
          toast.error(userErr.response?.data?.detail || 'Ошибка создания пользователя');
          // Manager was created but user wasn't - show partial success
        }
      }

      setDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Failed to save manager:', err);
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingManager) return;

    try {
      await deleteManager(deletingManager.id);
      toast.success('Менеджер удалён');
      setDeleteDialogOpen(false);
      setDeletingManager(null);
      fetchData();
    } catch (err) {
      console.error('Failed to delete manager:', err);
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const confirmDelete = (manager) => {
    setDeletingManager(manager);
    setDeleteDialogOpen(true);
  };

  const handleRestore = async (manager) => {
    try {
      await restoreManager(manager.id);
      toast.success('Менеджер восстановлен');
      fetchData();
    } catch (err) {
      console.error('Failed to restore manager:', err);
      toast.error(err.response?.data?.detail || 'Ошибка восстановления');
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Менеджеры</h1>
          <p className="text-muted-foreground">Управление менеджерами и доступом</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-manager-btn">
          <Plus className="h-4 w-4 mr-2" />
          Добавить
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid="active-managers-tab">
            <UserCircle className="h-4 w-4 mr-2" />
            Активные ({managers.length})
          </TabsTrigger>
          <TabsTrigger value="archived" data-testid="archived-managers-tab">
            <Archive className="h-4 w-4 mr-2" />
            Архив ({archivedManagers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          {managers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <UserCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Нет менеджеров</h3>
                <p className="text-muted-foreground mb-4">Добавьте первого менеджера</p>
                <Button onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Добавить менеджера
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {managers.map((manager) => (
                <Card key={manager.id} className="animate-fade-in" data-testid={`manager-${manager.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <UserCircle className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="font-medium">{manager.name}</h3>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditDialog(manager)}
                          data-testid={`edit-manager-${manager.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => confirmDelete(manager)}
                          data-testid={`delete-manager-${manager.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                        <Percent className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">До 1000</p>
                          <p className="font-medium">{manager.percent_standard}%</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                        <Percent className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Выше 1000</p>
                          <p className="font-medium">{manager.percent_above_1000}%</p>
                        </div>
                      </div>
                    </div>
                    {manager.team_name && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{manager.team_name}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="archived" className="mt-4">
          {archivedManagers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Archive className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Архив пуст</h3>
                <p className="text-muted-foreground">Удалённые менеджеры появятся здесь</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {archivedManagers.map((manager) => (
                <Card key={manager.id} className="animate-fade-in opacity-75" data-testid={`archived-manager-${manager.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <UserCircle className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="font-medium text-muted-foreground">{manager.name}</h3>
                          <span className="text-xs text-red-500">Удалён</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestore(manager)}
                        data-testid={`restore-manager-${manager.id}`}
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Восстановить
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                        <Percent className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">До 1000</p>
                          <p className="font-medium text-muted-foreground">{manager.percent_standard}%</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                        <Percent className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Выше 1000</p>
                          <p className="font-medium text-muted-foreground">{manager.percent_above_1000}%</p>
                        </div>
                      </div>
                    </div>
                    {manager.team_name && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{manager.team_name}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingManager ? 'Редактировать менеджера' : 'Добавить менеджера'}
            </DialogTitle>
            <DialogDescription>
              {editingManager 
                ? 'Измените данные менеджера' 
                : 'Создайте менеджера и укажите его логин/пароль для входа'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Имя менеджера</Label>
              <Input
                placeholder="Введите имя"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="manager-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>% до 1000</Label>
                <Input
                  type="number"
                  placeholder="10"
                  value={percentStandard}
                  onChange={(e) => setPercentStandard(e.target.value)}
                  min="0"
                  max="100"
                  data-testid="manager-percent-standard-input"
                />
              </div>
              <div className="space-y-2">
                <Label>% выше 1000</Label>
                <Input
                  type="number"
                  placeholder="15"
                  value={percentAbove1000}
                  onChange={(e) => setPercentAbove1000(e.target.value)}
                  min="0"
                  max="100"
                  data-testid="manager-percent-above-input"
                />
              </div>
            </div>

            {/* Team selection */}
            {teams.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Команда
                </Label>
                <Select value={teamId || "none"} onValueChange={(val) => setTeamId(val === "none" ? "" : val)}>
                  <SelectTrigger data-testid="manager-team-select">
                    <SelectValue placeholder="Выберите команду" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Без команды</SelectItem>
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Login credentials - for all managers */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <Key className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Данные для входа</span>
                {loadingUser && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Логин {!editingManager && '*'}</Label>
                  <Input
                    placeholder={editingManager ? "Оставьте пустым, чтобы не менять" : "Введите логин"}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    data-testid="manager-username-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Пароль {!editingManager && '*'}</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={editingManager ? "Оставьте пустым, чтобы не менять" : "Введите пароль"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                      data-testid="manager-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {editingManager && !existingUserId && (
                    <p className="text-xs text-muted-foreground">
                      У менеджера нет учётной записи. Заполните логин и пароль, чтобы создать.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name || !percentStandard || !percentAbove1000 || (!editingManager && (!username || !password)) || saving}
              data-testid="confirm-save-manager"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить менеджера?</AlertDialogTitle>
            <AlertDialogDescription>
              Менеджер &ldquo;{deletingManager?.name}&rdquo; будет перемещён в архив. 
              Вы сможете восстановить его позже. История доходов и зарплат сохранится.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-manager"
            >
              В архив
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
